import { cn } from '@/shared/lib/cn';
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { type FC, type ReactNode } from 'react';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const linkedHoverPlugin = (
  linkedPair?: [number, number]
): Plugin<'doughnut'> | null =>
  linkedPair
    ? {
        id: 'linkedSegmentHover',
        afterEvent(chart, args) {
          const event = args.event;

          if (event.type !== 'mousemove' && event.type !== 'mouseout') return;

          const active = chart.getActiveElements();
          const hoveredIndex = active[0]?.index ?? -1;

          const [a, b] = linkedPair;

          if (hoveredIndex === a || hoveredIndex === b) {
            const partner = hoveredIndex === a ? b : a;

            const alreadyLinked = active.some((el) => el.index === partner);

            if (!alreadyLinked) {
              chart.setActiveElements([
                ...active,
                { datasetIndex: 0, index: partner },
              ]);
              chart.update('none');
            }
          }
        },
      }
    : null;

interface DonutChartProps {
  data: ChartData<'doughnut'>;
  className?: string;
  innerContent: ReactNode;
  shouldLabelShowPercents?: boolean;
  additionalOptions?: ChartOptions<'doughnut'>;
  linkedPair?: [number, number];
}

const DonutChart: FC<DonutChartProps> = ({
  data,
  shouldLabelShowPercents = false,
  innerContent,
  className,
  additionalOptions = {},
  linkedPair,
}) => {
  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '75%',
    ...additionalOptions,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => {
            const raw = context.raw as number;
            return ` — ${raw.toFixed(1)}${shouldLabelShowPercents ? '%' : ''}`;
          },
          ...additionalOptions.plugins?.tooltip?.callbacks,
        },
        ...additionalOptions.plugins?.tooltip,
      },
      datalabels: { display: false },
      ...additionalOptions.plugins,
    },
  };

  const hoverPlugin = linkedHoverPlugin(linkedPair);
  const plugins = hoverPlugin ? [hoverPlugin] : [];

  return (
    <div className={cn('relative', className)}>
      <Doughnut
        className="relative z-[1]"
        data={data}
        options={options}
        plugins={plugins}
      />
      <div className="absolute top-1/2 left-1/2 z-0 flex w-fit -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center">
        {innerContent}
      </div>
    </div>
  );
};

export { DonutChart };
